import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeftIcon, GlobeIcon } from 'lucide-react';
import { useAui, useAuiState } from '@assistant-ui/react';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { cn } from '@/lib/utils';
import type { DomainMention } from '../utils/domainMentionMarkers';
import { useOptionalMcpComposerMentions } from './McpComposerMentionsContext';
import {
  filterDomains,
  McpDomainMenuBody,
  type McpUserDomain
} from './McpDomainMenu';
import { McpMenuShell, SEARCH_MENU_WIDTH } from './McpSearchMenu';

/** Trailing `@` mention token: `@` or `@query` (no spaces). */
const AT_TOKEN_RE = /(^|[\s\u200b])@([^\s]*)$/;

/** Compact root («دامنه‌ها»); wider submenu for domain address + display name. */
const ROOT_MENU_WIDTH = 140;
const MENU_GAP = 6;
const VIEWPORT_PAD = 12;

type AtMatch = {
  query: string;
  atIndex: number;
};

type MenuView = 'root' | 'domains';

function parseAtMatch(text: string): AtMatch | null {
  const match = text.match(AT_TOKEN_RE);
  if (!match || match.index == null) return null;
  const prefix = match[1] ?? '';
  return {
    query: match[2] ?? '',
    atIndex: match.index + prefix.length
  };
}

function setComposerText(
  aui: ReturnType<typeof useAui>,
  text: string
): void {
  try {
    const composer =
      typeof (aui as { composer?: unknown }).composer === 'function'
        ? (
            aui as {
              composer: () => { setText: (value: string) => void };
            }
          ).composer()
        : (
            aui as {
              composer: { setText: (value: string) => void };
            }
          ).composer;
    composer.setText(text);
    window.requestAnimationFrame(() => {
      const input = document.querySelector<HTMLTextAreaElement>(
        '.aui-composer-input, textarea[aria-label="Message input"]'
      );
      input?.focus();
      const cursor = text.length;
      input?.setSelectionRange(cursor, cursor);
    });
  } catch (error) {
    console.error('Failed to set composer text from @ mention', error);
  }
}

function getComposerAnchor(): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    '.aui-composer-root, [data-slot="aui_composer-shell"]'
  );
}

/**
 * Cursor-style `@` mention menu. Root has «دامنه‌ها»; drill-in lists user domains.
 * Selecting a domain adds an inline badge and removes the `@…` token.
 * Portaled into `.mcp-chat-page`: body portals sit outside the fullscreen
 * top layer (and under the page z-index), so the menu would not show.
 */
export const McpAtMentionMenu: FC = () => {
  const aui = useAui();
  const mentions = useOptionalMcpComposerMentions();
  const text = useAuiState(s => s.composer.text);
  const match = useMemo(() => parseAtMatch(text), [text]);
  const { data: userInfo, isLoading } = useGetUserInfoQuery();
  const domains = (userInfo?.data?.domains ?? []) as McpUserDomain[];

  const [view, setView] = useState<MenuView>('root');
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchFocusedRef = useRef(false);
  const wasOpenRef = useRef(false);

  const menuOpen = Boolean(match);

  const syncAnchor = useCallback(() => {
    const composer = getComposerAnchor();
    setAnchor(composer?.getBoundingClientRect() ?? null);
    setPortalHost(composer?.closest<HTMLElement>('.mcp-chat-page') ?? null);
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setAnchor(null);
      setPortalHost(null);
      return;
    }
    syncAnchor();
  }, [menuOpen, view, syncAnchor]);

  useEffect(() => {
    if (!menuOpen) return;
    const onReposition = () => syncAnchor();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [menuOpen, syncAnchor]);

  useEffect(() => {
    if (!match) {
      setView('root');
      setSearch('');
      wasOpenRef.current = false;
      return;
    }
    const q = match.query;
    if (!searchFocusedRef.current) {
      setSearch(q);
    }
    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      setView(q.trim() ? 'domains' : 'root');
      window.requestAnimationFrame(() => {
        if (q.trim()) {
          searchRef.current?.focus();
          searchRef.current?.select();
        }
      });
      return;
    }
    if (q.trim().length > 0) {
      setView('domains');
    }
  }, [match]);

  const filteredDomains = useMemo(
    () => filterDomains(domains, view === 'domains' ? search : ''),
    [domains, search, view]
  );

  const availableDomains = useMemo(() => {
    const selected = new Set(
      (mentions?.domainMentions ?? []).map(d => d.slug)
    );
    return filteredDomains.filter(d => !selected.has(d.slug));
  }, [filteredDomains, mentions?.domainMentions]);

  type RootItem = { id: 'domains'; title: string };
  const rootItems = useMemo<RootItem[]>(
    () => [{ id: 'domains', title: 'دامنه‌ها' }],
    []
  );

  const itemCount =
    view === 'root' ? rootItems.length : availableDomains.length;

  useEffect(() => {
    setActiveIndex(0);
  }, [search, menuOpen, view]);

  useEffect(() => {
    if (!menuOpen || itemCount === 0) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-at-index="${activeIndex}"]`
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, menuOpen, itemCount, view]);

  const clearAtToken = useCallback(() => {
    if (!match) return;
    setComposerText(aui, text.slice(0, match.atIndex).replace(/\s+$/, ''));
  }, [aui, match, text]);

  const dismissAt = useCallback(() => {
    clearAtToken();
  }, [clearAtToken]);

  const openDomains = useCallback(() => {
    setView('domains');
    setSearch('');
    setActiveIndex(0);
    window.requestAnimationFrame(() => {
      searchRef.current?.focus();
    });
  }, []);

  const selectDomain = useCallback(
    (domain: McpUserDomain) => {
      if (!match || !mentions) return;
      const mention: DomainMention = {
        slug: domain.slug,
        url: domain.url,
        show_name: domain.show_name
      };
      // Keep text before `@` as leading inline text; clear input to type after badge.
      const before = text.slice(0, match.atIndex);
      mentions.insertDomainMention(mention, before);
      setComposerText(aui, '');
    },
    [aui, match, mentions, text]
  );

  const activate = useCallback(() => {
    if (view === 'root') {
      const item = rootItems[activeIndex];
      if (item?.id === 'domains') openDomains();
      return;
    }
    const domain = availableDomains[activeIndex];
    if (domain) selectDomain(domain);
  }, [
    activeIndex,
    availableDomains,
    openDomains,
    rootItems,
    selectDomain,
    view
  ]);

  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as Node | null;
      const inMenu = Boolean(target && listRef.current?.contains(target));
      const inComposer = Boolean(
        target &&
          (target as HTMLElement).closest?.(
            '.aui-composer-root, .aui-composer-input'
          )
      );
      if (!inMenu && !inComposer) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (view === 'domains' && !(match?.query.trim())) {
          setView('root');
          setSearch('');
          return;
        }
        dismissAt();
        return;
      }

      if (event.key === 'Backspace' && view === 'domains') {
        const input = searchRef.current;
        if (
          input &&
          document.activeElement === input &&
          input.value === '' &&
          !(match?.query.trim())
        ) {
          event.preventDefault();
          setView('root');
          return;
        }
      }

      // Shift+Tab / ArrowRight: back from domain list to root (Cursor-style).
      if (
        view === 'domains' &&
        ((event.key === 'Tab' && event.shiftKey) || event.key === 'ArrowRight')
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setView('root');
        setSearch('');
        setActiveIndex(0);
        return;
      }

      if (itemCount === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setActiveIndex(i => (i + 1) % itemCount);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopImmediatePropagation();
        setActiveIndex(i => (i - 1 + itemCount) % itemCount);
        return;
      }

      // Tab / Enter / ArrowLeft: open submenu or pick domain (Cursor-style).
      if (
        event.key === 'Tab' ||
        event.key === 'Enter' ||
        (event.key === 'ArrowLeft' && view === 'root')
      ) {
        if (event.key === 'Tab' && event.shiftKey) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        activate();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [
    activate,
    dismissAt,
    itemCount,
    match?.query,
    menuOpen,
    view
  ]);

  const menuStyle: CSSProperties | undefined = anchor
    ? (() => {
        const menuWidth =
          view === 'domains' ? SEARCH_MENU_WIDTH : ROOT_MENU_WIDTH;
        const hostRect = portalHost?.getBoundingClientRect();
        const boundsTop = hostRect?.top ?? 0;
        const boundsLeft = hostRect?.left ?? 0;
        const boundsRight = hostRect?.right ?? window.innerWidth;
        const boundsBottom = hostRect?.bottom ?? window.innerHeight;
        const spaceAbove = Math.max(120, anchor.top - boundsTop - VIEWPORT_PAD);
        const maxHeight = Math.min(
          spaceAbove - MENU_GAP,
          (boundsBottom - boundsTop) * 0.45,
          240
        );
        // Prefer anchoring near the inline-start of the composer (right in RTL).
        const viewportLeft = Math.min(
          Math.max(boundsLeft + VIEWPORT_PAD, anchor.right - menuWidth),
          boundsRight - menuWidth - VIEWPORT_PAD
        );
        // Absolute inside the chat page: `position: fixed` on body is hidden
        // while that page is the fullscreen element.
        if (hostRect) {
          return {
            position: 'absolute',
            bottom: boundsBottom - anchor.top + MENU_GAP,
            left: viewportLeft - boundsLeft,
            width: menuWidth,
            maxHeight,
            zIndex: 1080
          };
        }
        return {
          position: 'fixed',
          bottom: window.innerHeight - anchor.top + MENU_GAP,
          left: viewportLeft,
          width: menuWidth,
          maxHeight,
          zIndex: 1080
        };
      })()
    : undefined;

  if (!menuOpen || !mentions || !anchor || !menuStyle) return null;

  const backToRoot = () => {
    setView('root');
    setSearch('');
  };

  return createPortal(
    <McpMenuShell
      ref={listRef}
      ariaLabel="منشن"
      style={menuStyle}
      className={cn(view === 'root' && 'mcp-at-mention-menu--root')}
    >
      {view === 'root' ? (
        <ul className="list-unstyled mb-0 p-1 mcp-at-mention-menu__list">
          {rootItems.map((item, index) => {
            const active = index === activeIndex;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  data-at-index={index}
                  className={cn(
                    'mcp-picker-item mcp-at-mention-item d-flex w-100 align-items-center justify-content-between gap-1 border-0 rounded-2 text-start',
                    active && 'is-selected'
                  )}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={e => e.preventDefault()}
                  onClick={openDomains}
                >
                  <span className="d-inline-flex align-items-center gap-2 min-w-0">
                    <GlobeIcon
                      size={16}
                      strokeWidth={2}
                      className="flex-shrink-0 opacity-80"
                      aria-hidden
                    />
                    <span className="fs--1 fw-medium text-truncate">
                      {item.title}
                    </span>
                  </span>
                  <ChevronLeftIcon
                    size={14}
                    strokeWidth={2}
                    className="flex-shrink-0 opacity-45"
                    aria-hidden
                  />
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <McpDomainMenuBody
          domains={availableDomains}
          totalCount={domains.length}
          isLoading={isLoading}
          search={search}
          onSearchChange={setSearch}
          searchRef={searchRef}
          searchInputProps={{
            onFocus: () => {
              searchFocusedRef.current = true;
            },
            onBlur: () => {
              searchFocusedRef.current = false;
            },
            onKeyDown: e => {
              if (
                e.key === 'ArrowDown' ||
                e.key === 'ArrowUp' ||
                e.key === 'Enter' ||
                e.key === 'Escape'
              ) {
                return;
              }
              e.stopPropagation();
            }
          }}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          onSelect={selectDomain}
          onBack={backToRoot}
        />
      )}
    </McpMenuShell>,
    portalHost ?? document.body
  );
};
